import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './VehicleDetail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
// Utility to calculate monthly payment for a loan
function calculateMonthlyPayment(principal, apr, years, deposit = 0) {
  const P = principal - deposit;
  const monthlyRate = apr / 12;
  const n = years * 12;
  if (monthlyRate === 0) return P / n;
  return P * monthlyRate / (1 - Math.pow(1 + monthlyRate, -n));
}

const VehicleDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const scrollRef = useRef(null);
  const [showFinanceForm, setShowFinanceForm] = useState(false);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    const idx = Math.round(scrollLeft / clientWidth);
    setCurrentImageIndex(idx);
  };

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`/api/vehicles/${id}`);
        setVehicle(response.data);
      } catch (error) {
        console.error('Error fetching vehicle details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  if (loading) {
    return <p>Loading vehicle details...</p>;
  }

  if (!vehicle) {
    return <p>Vehicle not found.</p>;
  }

  const make = vehicle?.vehicle?.make || 'Unknown';
  const model = vehicle?.vehicle?.model || 'Unknown';
  const derivative = vehicle?.vehicle?.derivative || 'Unknown';
  const yearOfManufacture = vehicle?.vehicle?.yearOfManufacture || 'N/A';
  const odometerReadingMiles = vehicle?.vehicle?.odometerReadingMiles || 'N/A';
  const transmissionType = vehicle?.vehicle?.transmissionType || 'N/A';
  const fuelType = vehicle?.vehicle?.fuelType || 'N/A';
  const bodyType = vehicle?.vehicle?.bodyType || 'N/A';
  const engineSize = vehicle?.vehicle?.engineSize || 'N/A';
  const doors = vehicle?.vehicle?.doors || 'N/A';
  const seats = vehicle?.vehicle?.seats || 'N/A';
  const serviceHistory = vehicle?.vehicle?.serviceHistory || 'N/A';
  const emissionClass = vehicle?.vehicle?.emissionClass || 'N/A';
  const owners = vehicle?.vehicle?.owners || 'N/A';
  const images = vehicle.media?.images || [{ href: 'placeholder.jpg' }];
  const priceGBP = vehicle.adverts?.retailAdverts?.totalPrice?.amountGBP || 'N/A';
  const vatStatus = vehicle.adverts?.retailAdverts?.vatStatus;
  // Handle VAT: if Ex Vat or + VAT, add 20% before calculation
  const rawPrice = Number(priceGBP);
  const isExVat = vatStatus && (/Ex Vat|\+ VAT/i).test(vatStatus);
  const priceForCalculation = isExVat ? rawPrice * 1.2 : rawPrice;
  const aprRepresentative = 0.205;
  const termYears = 5;
  const deposit = 0;
  const optionFee = 10;
  const monthlyPayment = calculateMonthlyPayment(priceForCalculation, aprRepresentative, termYears, deposit);
  const termMonths = termYears * 12;
  const totalCostCredit = monthlyPayment * termMonths - (priceForCalculation - deposit) + optionFee;
  const totalAmountRepayable = monthlyPayment * termMonths + optionFee;

  return (
    <div className="vehicle-detail-container">
      <div className="vehicle-detail">
        <div className="vehicle-detail__images">
          {/* ===== MOBILE: swipe gallery ===== */}
          <div className="vehicle-detail__image-wrapper mobile-only">
            <div
              className="vehicle-detail__image-scroll"
              ref={scrollRef}
              onScroll={handleScroll}
            >
              {images.map((img, idx) => (
                <img
                  key={idx}
                  className="vehicle-detail__scroll-image"
                  src={img.href}
                  alt={`${make} ${model} ${idx + 1}`}
                />
              ))}
            </div>
            <div className="vehicle-detail__image-count">
              {currentImageIndex + 1}/{images.length}
            </div>
          </div>

          {/* ===== DESKTOP: main image + thumbnails ===== */}
          <div className="vehicle-detail__desktop-images desktop-only">
            <div className="vehicle-detail__main-image-wrapper">
              <img
                className="vehicle-detail__main-image"
                src={images[currentImageIndex].href}
                alt={`${make} ${model}`}
              />
            </div>
            <div className="vehicle-detail__thumbnail-row">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  className={`vehicle-detail__thumbnail ${
                    idx === currentImageIndex ? 'selected' : ''
                  }`}
                  src={img.href}
                  alt={`${make} ${model} thumbnail ${idx + 1}`}
                  onClick={() => setCurrentImageIndex(idx)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="vehicle-detail__info">
          <div className="vehicle-detail__title">
            <span className="vehicle-detail__make-model">{make} {model}</span>
            <span className="vehicle-detail__derivative">{derivative}</span>
          </div>
          {/* Styled List Items */}
          <ul className="vehicle-detail__specs">
            <li className="vehicle-detail__spec-item">{yearOfManufacture}</li>
            <li className="vehicle-detail__spec-item">{odometerReadingMiles} miles</li>
            <li className="vehicle-detail__spec-item">{transmissionType}</li>
            <li className="vehicle-detail__spec-item">{fuelType}</li>
          </ul>
          <div className="vehicle-detail__price">£{priceGBP} {vatStatus && <span>({vatStatus})</span>}</div>
          <p className="vehicle-detail__monthly-price">
            Monthly Prices From: £{monthlyPayment.toFixed(2)}
          </p>
          <button className="vehicle-detail__apply-finance"
            onClick={() => setShowFinanceForm(true)}
          >
            Apply for Finance
          </button>
          <div className="vehicle-detail__contact-options">
            <a href="tel:01516731916" className="contact-icon detail">
              <FontAwesomeIcon icon={faPhone} />
            </a>
            <a href="mailto:rental@vanirmotorgroup.com" className="contact-icon detail">
              <FontAwesomeIcon icon={faEnvelope} />
            </a>
            <a
              href="https://wa.me/447960045554"
              className="contact-icon detail"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </a>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="vehicle-detail__overview">
      <div className="vehicle-detail__rep-example">
        <p>Rates from 8.9% APR. Representative Example: Borrow £12,000 over 5 years with a £0 deposit. Representative 20.5% APR fixed rate.</p>
        <p>Monthly payment: £309.93</p>
        <p>Option to purchase fee: £10 payable</p>
        <p>Total cost of credit: £6,605.80</p>
        <p>Total amount repayable: £18,605.80</p>
        <p>Vanir Motor Group is a credit broker, not a lender.</p>
      </div>
        <h2>Overview</h2>
        <div className="vehicle-detail__overview-top">
          <div className="vehicle-detail__overview-item">
            <div className="vehicle-detail__overview-icon">🚗</div>
            <div className="vehicle-detail__overview-content">
              <h3>Mileage</h3>
              <p>{odometerReadingMiles} miles</p>
              <p className="vehicle-detail__mileage-note">2,847 miles above average</p>
            </div>
          </div>
          <div className="vehicle-detail__overview-item">
            <div className="vehicle-detail__overview-icon">📅</div>
            <div className="vehicle-detail__overview-content">
              <h3>Registration</h3>
              <p>{yearOfManufacture}</p>
            </div>
          </div>
          <div className="vehicle-detail__overview-item">
            <div className="vehicle-detail__overview-icon">👤</div>
            <div className="vehicle-detail__overview-content">
              <h3>Owners</h3>
              <p>{owners}</p>
            </div>
          </div>
        </div>

        <div className="vehicle-detail__specs-list">
          <div className="vehicle-detail__spec-row"><span>Fuel type</span><span>{fuelType}</span></div>
          <div className="vehicle-detail__spec-row"><span>Body type</span><span>{bodyType}</span></div>
          <div className="vehicle-detail__spec-row"><span>Engine</span><span>{engineSize}</span></div>
          <div className="vehicle-detail__spec-row"><span>Gearbox</span><span>{transmissionType}</span></div>
          <div className="vehicle-detail__spec-row"><span>Doors</span><span>{doors}</span></div>
          <div className="vehicle-detail__spec-row"><span>Seats</span><span>{seats}</span></div>
          <div className="vehicle-detail__spec-row"><span>Service history</span><span>{serviceHistory}</span></div>
          <div className="vehicle-detail__spec-row"><span>Emission class</span><span>{emissionClass}</span></div>
        </div>
      </div>
      <FinanceApplicationForm
        visible={showFinanceForm}
        onClose={() => setShowFinanceForm(false)}
        vehicle={vehicle}
      />
    </div>
  );
};

// ── Finance Application Multi-Step Form ──
const FinanceApplicationForm = ({ visible, onClose, vehicle }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    licenseType: '',
    firstName: '', surname: '', phone: '', email: '', dob: '',
    residentialStatus: '',
    maritalStatus: '',
    address1: '', address2: '', city: '', postcode: '',
    occupationalStatus: '',
    employer: '', jobTitle: '', workYears: '', workMonths: '', netIncome: ''
  });
  const [residenceYears, setResidenceYears] = useState('');
  const [residenceMonths, setResidenceMonths] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await axios.post(
        'https://leadtrackcrm.com/api/finance-application',
        {
          vehicleId: vehicle._id,
          vehicleDetails: {
            make: vehicle.vehicle.make,
            model: vehicle.vehicle.model,
            registration: vehicle.vehicle.registration,
            mileage: vehicle.vehicle.odometerReadingMiles
          },
          ...formData
        }
      );
      alert('Application submitted!');
      onClose();
    } catch (err) {
      console.error(err);
      alert('Submission failed—please try again.');
    }
  };

  return (
    <div className={`finance-form-overlay${visible ? ' open' : ''}`}>
      <div className="finance-form">
        <button
          type="button"
          className="finance-form__close"
          onClick={onClose}
        >
          ×
        </button>
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              <h2>
                Applying for finance on:&nbsp;
                {vehicle.vehicle.make}, {vehicle.vehicle.model},&nbsp;
                {vehicle.vehicle.registration},&nbsp;
                {vehicle.vehicle.odometerReadingMiles} miles
              </h2>
              <label className="finance-form__question">
                What kind of Driving Licence do you have?
              </label>
              <div className="finance-form__license-options">
                {[
                  'Full UK',
                  'Provisional UK',
                  'CBT',
                  'A2',
                  'Full A Class',
                  'European',
                  'International',
                  'None'
                ].map(option => (
                  <label
                    key={option}
                    className={`finance-form__license-option ${formData.licenseType === option ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="licenseType"
                      value={option}
                      checked={formData.licenseType === option}
                      onChange={handleChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <button type="button" className="finance-form__next" onClick={() => setStep(2)}>Next</button>
            </>
          )}

          {step === 2 && (
            <>
              <h2>Personal Details</h2>
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <label htmlFor="surname">Surname</label>
              <input
                type="text"
                name="surname"
                id="surname"
                value={formData.surname}
                onChange={handleChange}
              />
              <label htmlFor="phone">Telephone</label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
              />
              <label htmlFor="email">Email</label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={e => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              />
              <label htmlFor="dob">DOB</label>
              <input
                type="date"
                name="dob"
                id="dob"
                value={formData.dob}
                onChange={handleChange}
                onFocus={e => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              />
              <button type="button" onClick={() => setStep(3)}>Next</button>
            </>
          )}

          {step === 3 && (
            <>
              <label className="finance-form__question">
                What is your marital status?
              </label>
              <div className="finance-form__license-options finance-form__marital-options">
                {[
                  'Single','Cohabiting','Married','Separated','Divorced',
                  'Widowed','Civil Partnership','Dissolved Civil Partnership','Engaged'
                ].map(option => (
                  <label
                    key={option}
                    className={`finance-form__license-option ${
                      formData.maritalStatus === option ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="maritalStatus"
                      value={option}
                      checked={formData.maritalStatus === option}
                      onChange={handleChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="finance-form__next"
                onClick={() => setStep(4)}
              >
                Next
              </button>
            </>
          )}

          {step === 4 && (
            <>
              <label className="finance-form__question">
                What is your Residential status?
              </label>
              <div className="finance-form__license-options finance-form__residential-options">
                {[
                  'Homeowner','Tenant - Private','Tenant - Housing Association','Tenant - Council','Living With Family','Military Accommodation','Work Accommodation','Student Accommodation'
                ].map(option => (
                  <label
                    key={option}
                    className={`finance-form__license-option ${formData.residentialStatus === option ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="residentialStatus"
                      value={option}
                      checked={formData.residentialStatus === option}
                      onChange={handleChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <button type="button" className="finance-form__next" onClick={() => setStep(5)}>Next</button>
            </>
          )}

          {step === 5 && (
            <>
              <h2>Residential Address</h2>
              <label htmlFor="address1">1st Line of Address</label>
              <input
                type="text"
                name="address1"
                id="address1"
                value={formData.address1}
                onChange={handleChange}
              />
              <label htmlFor="address2">2nd Line of Address</label>
              <input
                type="text"
                name="address2"
                id="address2"
                value={formData.address2}
                onChange={handleChange}
              />
              <label htmlFor="city">City / Town</label>
              <input
                type="text"
                name="city"
                id="city"
                value={formData.city}
                onChange={handleChange}
              />
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                name="postcode"
                id="postcode"
                value={formData.postcode}
                onChange={handleChange}
              />
              <label htmlFor="residence-duration">How long have you lived here?</label>
              <div className="duration-inputs">
                <input
                  type="number"
                  id="residenceYears"
                  name="residenceYears"
                  placeholder="Years"
                  value={residenceYears}
                  onChange={e => setResidenceYears(e.target.value)}
                />
                <input
                  type="number"
                  id="residenceMonths"
                  name="residenceMonths"
                  placeholder="Months"
                  value={residenceMonths}
                  onChange={e => setResidenceMonths(e.target.value)}
                />
              </div>
              <button type="button" onClick={() => setStep(6)}>Next</button>
            </>
          )}

          {step === 6 && (
            <>
              <label className="finance-form__question">
                What is your occupational status?
              </label>
              <div className="finance-form__license-options finance-form__occupational-options">
                {[
                  'Full-Time Employment','Part-Time Employment','Self-Employed',
                  'Temporary/ Contract','Retired','Homemaker','Carer',
                  'Education','Benefits','Armed Services'
                ].map(option => (
                  <label
                    key={option}
                    className={`finance-form__license-option ${
                      formData.occupationalStatus === option ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="occupationalStatus"
                      value={option}
                      checked={formData.occupationalStatus === option}
                      onChange={handleChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="finance-form__next"
                onClick={() => setStep(7)}
              >
                Next
              </button>
            </>
          )}

          {step === 7 && (
            <>
              <h2>Employment</h2>
              <label htmlFor="employer">
                Who is your employer? <span title="We never contact your employer">ℹ️</span>
              </label>
              <input
                type="text"
                name="employer"
                id="employer"
                placeholder="Company name here"
                value={formData.employer}
                onChange={handleChange}
              />
              <label htmlFor="jobTitle">Job Title</label>
              <input
                type="text"
                name="jobTitle"
                id="jobTitle"
                placeholder="Job title here"
                value={formData.jobTitle}
                onChange={handleChange}
              />
              <label>How long have you worked there</label>
              <div style={{ display: 'flex', gap: '4%' }}>
                <input
                  type="number"
                  name="workYears"
                  placeholder="Years"
                  value={formData.workYears}
                  onChange={handleChange}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  name="workMonths"
                  placeholder="Months"
                  value={formData.workMonths}
                  onChange={handleChange}
                  style={{ flex: 1 }}
                />
              </div>
              <label htmlFor="netIncome">Total NET monthly income</label>
              <input
                type="number"
                name="netIncome"
                id="netIncome"
                value={formData.netIncome}
                onChange={handleChange}
              />
              <button type="submit">Submit</button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default VehicleDetail;
